/**
 * MeuNegocioLiteModal — painel simplificado "Meu Negócio" acessível para o dono
 * direto da página pública do negócio.
 *
 * Difere do editor admin em NegocioEditorCompleto.js:
 *   - Sem campos técnicos de moderação (status, notas, etc.)
 *   - Sem localização/coordenadas
 *   - Apenas: descrição, contato, horários, imagens (logo + galeria)
 *
 * Como salva:
 *   Faz PUT /api/businesses/{id} enviando TODOS os campos atuais do negócio
 *   (spread) + apenas os campos que o dono editou. Isso satisfaz o schema
 *   BusinessCreate (que exige nome, categoria, endereço, latitude, etc.)
 *   sem forçar o dono a rever esses campos.
 */
import React, { useEffect, useState } from 'react';
import { X, Save, Loader2, Info, Phone, Clock, ImageIcon, Eye, MessageCircle, Heart, Share2, TrendingUp, Crop } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import ImageUploader from './ImageUploader';
import ImageCropModal from './ImageCropModal';
import { resolveBackendImage } from '../utils/brandLogo';

const DIAS = [
  ['segunda', 'Segunda'], ['terca', 'Terça'], ['quarta', 'Quarta'],
  ['quinta', 'Quinta'], ['sexta', 'Sexta'], ['sabado', 'Sábado'],
  ['domingo', 'Domingo'],
];

const HORARIOS_PADRAO = DIAS.map(([dia]) => ({
  dia, abre: '08:00', fecha: '18:00', fechado: dia === 'domingo',
}));

const TABS = [
  { key: 'info', label: 'Informações', icon: Info },
  { key: 'contato', label: 'Contato', icon: Phone },
  { key: 'horarios', label: 'Horários', icon: Clock },
  { key: 'imagens', label: 'Imagens', icon: ImageIcon },
];

function StatCard({ icon: Icon, label, value, delta, tone = 'purple', testid }) {
  const toneMap = {
    purple: 'bg-purple-50 text-purple-700 ring-purple-100',
    green: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    pink: 'bg-pink-50 text-pink-700 ring-pink-100',
    blue: 'bg-blue-50 text-blue-700 ring-blue-100',
  };
  return (
    <div
      className={`rounded-xl ring-1 ${toneMap[tone]} px-3 py-2 flex flex-col gap-0.5`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-lg font-bold leading-none" data-testid={`${testid}-value`}>
        {value ?? '—'}
      </div>
      {typeof delta === 'number' && (
        <div className="text-[10px] font-medium opacity-70 flex items-center gap-0.5">
          <TrendingUp className="w-3 h-3" />
          {delta > 0 ? `+${delta} nos últimos 7 dias` : 'sem movimento nos últimos 7 dias'}
        </div>
      )}
    </div>
  );
}

function StatsWidget({ stats, loading }) {
  if (loading) {
    return (
      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3 flex items-center gap-2 text-sm text-gray-500" data-testid="lite-stats-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Carregando estatísticas…
      </div>
    );
  }
  if (!stats) return null;
  return (
    <div className="space-y-2" data-testid="lite-stats-widget">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Desempenho do seu negócio
        </h3>
        <span className="text-[10px] text-gray-400">últimos 7 dias</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard
          icon={Eye}
          label="Visitas"
          value={stats.views_total}
          delta={stats.views_7d}
          tone="purple"
          testid="lite-stat-views"
        />
        <StatCard
          icon={MessageCircle}
          label="Cliques WhatsApp"
          value={stats.whatsapp_clicks_total}
          delta={stats.whatsapp_clicks_7d}
          tone="green"
          testid="lite-stat-whatsapp"
        />
        <StatCard
          icon={Heart}
          label="Favoritos"
          value={stats.favorites_total}
          tone="pink"
          testid="lite-stat-favoritos"
        />
        <StatCard
          icon={Share2}
          label="Compartilhamentos"
          value={stats.shares_total}
          delta={stats.shares_7d}
          tone="blue"
          testid="lite-stat-shares"
        />
      </div>
    </div>
  );
}

export default function MeuNegocioLiteModal({ business, onClose, onSaved }) {
  const [tab, setTab] = useState('info');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    descricao_curta: business.descricao_curta || '',
    descricao: business.descricao || '',
    telefone: business.telefone || '',
    telefone_fixo: business.telefone_fixo || '',
    whatsapp: business.whatsapp || '',
    instagram: business.instagram || '',
    site: business.site || '',
    email: business.email || '',
    horarios: (business.horarios && business.horarios.length > 0)
      ? DIAS.map(([dia]) => {
          const h = business.horarios.find((x) => x.dia === dia);
          return h ? { dia, abre: h.abre || '', fecha: h.fecha || '', fechado: !!h.fechado }
                   : { dia, abre: '', fecha: '', fechado: true };
        })
      : [...HORARIOS_PADRAO],
    horarios_especiais: business.horarios_especiais || '',
  }));
  const [imagens, setImagens] = useState(business.imagens || { logo: null, galeria: [] });
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  // Reajuste de logo: reabre o ImageCropModal com a imagem ATUAL do negócio
  // (baixada como blob) + crop_data salvo, para o dono refinar o
  // enquadramento sem precisar de novo upload.
  const [readjustFile, setReadjustFile] = useState(null);
  const [readjustBusy, setReadjustBusy] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/businesses/${business.id}/lite-stats`);
        if (!cancelled) setStats(data);
      } catch (err) {
        // Silencia: stats é secundário. Modal continua utilizável para edição.
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoadingStats(false);
      }
    })();
    return () => { cancelled = true; };
  }, [business.id]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const setHorario = (idx, key, value) => {
    setForm((prev) => {
      const next = [...prev.horarios];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, horarios: next };
    });
  };

  // Abre o modal de recorte com a logo atual (baixa como blob para virar File)
  const handleReadjustLogo = async () => {
    const logo = imagens?.logo;
    const src = logo?.large || logo?.medium || logo?.thumb || business.avatar_url;
    if (!src) {
      toast.error('Nenhuma logo para reajustar.');
      return;
    }
    setReadjustBusy(true);
    try {
      const resp = await fetch(resolveBackendImage(src));
      if (!resp.ok) throw new Error('Falha ao carregar a logo atual.');
      const blob = await resp.blob();
      const ext = (blob.type || '').includes('png') ? 'png' : 'webp';
      const file = new File([blob], `logo-atual.${ext}`, { type: blob.type || 'image/webp' });
      setReadjustFile(file);
    } catch (err) {
      toast.error(err.message || 'Não foi possível abrir a logo para reajuste.');
    } finally {
      setReadjustBusy(false);
    }
  };

  // Envia o blob recortado para o mesmo endpoint /upload/imagem que o
  // ImageUploader usa — o backend substitui a logo atual (imagens.logo) e
  // também sincroniza avatar_url/logo_url legados (fix da iter 188).
  const handleReadjustConfirm = async ({ blob, cropData, outputType }) => {
    setReadjustFile(null);
    try {
      const ext = outputType === 'image/png' ? 'png' : 'webp';
      const file = new File([blob], `logo-reajustada.${ext}`, { type: outputType });
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('municipio_id', business.municipio_id || '');
      fd.append('entidade_id', business.id);
      fd.append('area', 'negocio');
      fd.append('tipo', 'logo');
      fd.append('formato', 'quadrado');
      fd.append('crop', JSON.stringify(cropData || {}));
      const res = await api.post('/upload/imagem', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploaded = res?.data?.imagem || res?.data;
      if (uploaded) {
        setImagens((prev) => ({ ...prev, logo: uploaded }));
      }
      toast.success('Logo reajustada com sucesso!');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erro ao reajustar logo.';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao reajustar logo.');
    }
  };

  const handleSave = async () => {
    if (!form.descricao || form.descricao.trim().length < 10) {
      toast.error('A descrição deve ter pelo menos 10 caracteres.');
      setTab('info');
      return;
    }
    if (!form.whatsapp || form.whatsapp.trim().length < 8) {
      toast.error('WhatsApp é obrigatório para contato.');
      setTab('contato');
      return;
    }

    setSaving(true);
    try {
      // PATCH parcial exclusivo do painel Lite. NÃO precisa espelhar
      // BusinessCreate — o backend aceita apenas os campos alterados e
      // não revalida CPF/CNPJ, localização ou categoria.
      const payload = {
        descricao_curta: form.descricao_curta,
        descricao: form.descricao,
        telefone: form.telefone || null,
        telefone_fixo: form.telefone_fixo || null,
        whatsapp: form.whatsapp,
        email: form.email || null,
        site: form.site || null,
        instagram: form.instagram || null,
        horarios: form.horarios,
        horarios_especiais: form.horarios_especiais || null,
      };

      const { data } = await api.patch(`/businesses/${business.id}/lite`, payload);
      toast.success('Negócio atualizado com sucesso!');
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erro ao salvar.';
      toast.error(typeof msg === 'string' ? msg : 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true">
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-full sm:max-h-[90vh]"
        data-testid="meu-negocio-lite-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 flex-shrink-0">
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>Editar meu negócio</h2>
            <p className="text-xs text-gray-500 truncate max-w-[70vw]">{business.nome}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            data-testid="meu-negocio-lite-close"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-gray-200 flex-shrink-0">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === key
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
              data-testid={`meu-negocio-lite-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Mini-widget de estatísticas — sempre visível no topo */}
          <StatsWidget stats={stats} loading={loadingStats} />

          {tab === 'info' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Descrição curta</label>
                <input
                  type="text"
                  maxLength={250}
                  value={form.descricao_curta}
                  onChange={(e) => setField('descricao_curta', e.target.value)}
                  placeholder="Uma frase que resume seu negócio (até 250 caracteres)"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
                  data-testid="lite-input-descricao-curta"
                />
                <p className="text-xs text-gray-500 mt-1">{form.descricao_curta.length}/250</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrição completa *</label>
                <textarea
                  rows={5}
                  value={form.descricao}
                  onChange={(e) => setField('descricao', e.target.value)}
                  placeholder="Conte sobre seu negócio, produtos, diferenciais…"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
                  data-testid="lite-input-descricao"
                />
              </div>
            </>
          )}

          {tab === 'contato' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">WhatsApp *</label>
                  <input
                    type="tel"
                    value={form.whatsapp}
                    onChange={(e) => setField('whatsapp', e.target.value)}
                    placeholder="(31) 99999-9999"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    data-testid="lite-input-whatsapp"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Telefone celular</label>
                  <input
                    type="tel"
                    value={form.telefone}
                    onChange={(e) => setField('telefone', e.target.value)}
                    placeholder="(31) 99999-9999"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    data-testid="lite-input-telefone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Telefone fixo</label>
                  <input
                    type="tel"
                    value={form.telefone_fixo}
                    onChange={(e) => setField('telefone_fixo', e.target.value)}
                    placeholder="(31) 3333-4444"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    data-testid="lite-input-telefone-fixo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">E-mail</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="contato@..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    data-testid="lite-input-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Instagram</label>
                  <input
                    type="text"
                    value={form.instagram}
                    onChange={(e) => setField('instagram', e.target.value)}
                    placeholder="@usuario"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    data-testid="lite-input-instagram"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Site</label>
                  <input
                    type="url"
                    value={form.site}
                    onChange={(e) => setField('site', e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    data-testid="lite-input-site"
                  />
                </div>
              </div>
            </>
          )}

          {tab === 'horarios' && (
            <div className="space-y-2">
              {form.horarios.map((h, idx) => {
                const label = DIAS.find(([d]) => d === h.dia)?.[1] || h.dia;
                return (
                  <div
                    key={h.dia}
                    className="flex flex-wrap items-center gap-2 sm:gap-3 p-2 rounded-lg border border-gray-100"
                    data-testid={`lite-horario-${h.dia}`}
                  >
                    <div className="w-20 sm:w-24 text-sm font-medium">{label}</div>
                    <label className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={h.fechado}
                        onChange={(e) => setHorario(idx, 'fechado', e.target.checked)}
                        data-testid={`lite-horario-${h.dia}-fechado`}
                      />
                      Fechado
                    </label>
                    <input
                      type="time"
                      value={h.abre || ''}
                      onChange={(e) => setHorario(idx, 'abre', e.target.value)}
                      disabled={h.fechado}
                      className="px-2 py-1 rounded border border-gray-300 text-sm disabled:opacity-50"
                      data-testid={`lite-horario-${h.dia}-abre`}
                    />
                    <span className="text-gray-400">até</span>
                    <input
                      type="time"
                      value={h.fecha || ''}
                      onChange={(e) => setHorario(idx, 'fecha', e.target.value)}
                      disabled={h.fechado}
                      className="px-2 py-1 rounded border border-gray-300 text-sm disabled:opacity-50"
                      data-testid={`lite-horario-${h.dia}-fecha`}
                    />
                  </div>
                );
              })}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Observações de horário (opcional)</label>
                <textarea
                  rows={2}
                  value={form.horarios_especiais}
                  onChange={(e) => setField('horarios_especiais', e.target.value)}
                  placeholder="Ex.: Fechado em feriados nacionais."
                  className="w-full px-3 py-2 rounded-lg border border-gray-300"
                  data-testid="lite-input-horarios-especiais"
                />
              </div>
            </div>
          )}

          {tab === 'imagens' && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-medium">Logo</div>
                    <div className="text-xs text-gray-500">Uma imagem quadrada (recomendado 800×800).</div>
                  </div>
                  {(imagens?.logo?.medium || imagens?.logo?.thumb || business.avatar_url) && (
                    <button
                      onClick={handleReadjustLogo}
                      disabled={readjustBusy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-60"
                      data-testid="lite-reajustar-logo-btn"
                      title="Reabrir o editor de recorte com a logo atual"
                    >
                      {readjustBusy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Crop className="w-3.5 h-3.5" />
                      )}
                      Reajustar logo
                    </button>
                  )}
                </div>
                <ImageUploader
                  area="negocio"
                  tipo="logo"
                  formato="quadrado"
                  entidadeId={business.id}
                  municipioId={business.municipio_id}
                  imagensAtuais={imagens.logo}
                  single
                  onChange={(imgs) => {
                    const logo = Array.isArray(imgs) ? imgs[0] : imgs;
                    setImagens((prev) => ({ ...prev, logo: logo || null }));
                  }}
                  label=""
                  description=""
                />
              </div>
              <div>
                <ImageUploader
                  area="negocio"
                  tipo="galeria"
                  formato="quadrado"
                  entidadeId={business.id}
                  municipioId={business.municipio_id}
                  imagensAtuais={imagens.galeria || []}
                  onChange={(imgs) => setImagens((prev) => ({ ...prev, galeria: imgs || [] }))}
                  label="Galeria"
                  description="Fotos que aparecem na página do negócio."
                />
              </div>
              <p className="text-xs text-gray-500">
                Imagens são salvas automaticamente ao enviar. Clique em <strong>Salvar alterações</strong> para atualizar os demais campos.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-end gap-2 flex-shrink-0 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
            data-testid="lite-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="lite-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar alterações
          </button>
        </div>
      </div>

      {/* Modal de recorte reaproveitando a logo atual */}
      <ImageCropModal
        open={!!readjustFile}
        file={readjustFile}
        mode="logo"
        onConfirm={handleReadjustConfirm}
        onCancel={() => setReadjustFile(null)}
      />
    </div>
  );
}
